import { config } from '@/config';
import type { KeycloakTokenParsed } from 'keycloak-js';
import type { UserInfo } from '@/types';

type TokenParsed = KeycloakTokenParsed & {
  realm_access?: { roles?: unknown };
  resource_access?: Record<string, { roles?: unknown }>;
  authorization?: { permissions?: unknown };
  scope?: string;
};

const normalizeToken = (value: string) => value.trim().toLowerCase();

const unique = (values: string[]) => Array.from(new Set(values.map(normalizeToken)));

const resolveClaim = (payload: Record<string, unknown>, path: string): unknown => {
  const parts = path.split('.').filter(Boolean);
  let current: unknown = payload;

  for (const part of parts) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }

    current = (current as Record<string, unknown>)[part];
  }

  return current;
};

const toStringArray = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return value
      .split(/[,\s;|]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      if (typeof entry === 'string') {
        return entry.trim() ? [entry.trim()] : [];
      }

      if (entry && typeof entry === 'object') {
        const scopes = (entry as { scopes?: unknown }).scopes;
        return toStringArray(scopes);
      }

      return [];
    });
  }

  return [];
};

export const buildUserInfoFromTokenParsed = (
  tokenParsed: KeycloakTokenParsed | undefined
): UserInfo | null => {
  if (!tokenParsed?.email) {
    return null;
  }

  const parsed = tokenParsed as TokenParsed;
  const clientId = config().keycloak.clientId;
  const realmRoles = toStringArray(resolveClaim(parsed as unknown as Record<string, unknown>, 'realm_access.roles'));
  const clientRoles = toStringArray(
    resolveClaim(parsed as unknown as Record<string, unknown>, `resource_access.${clientId}.roles`)
  );
  const permissionClaims = toStringArray(
    resolveClaim(parsed as unknown as Record<string, unknown>, 'authorization.permissions')
  );
  const scopes = toStringArray(parsed.scope);
  const roles = unique([...realmRoles, ...clientRoles]);
  const permissions = unique([...roles, ...permissionClaims, ...scopes]);

  return {
    email: tokenParsed.email,
    sub: tokenParsed.sub,
    roles,
    permissions,
  };
};
